import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

type SortOption = "default" | "price-asc" | "price-desc" | "name-asc" | "name-desc" | "newest" | "discount-desc";

const defaultSortOptions: { value: SortOption; label: string }[] = [
  { value: "default", label: "Popularity" },
  { value: "price-desc", label: "High – Low Price" },
  { value: "price-asc", label: "Low – High Price" },
  { value: "newest", label: "Newest" },
];

const saleSortOptions: { value: SortOption; label: string }[] = [
  { value: "default", label: "Popularity" },
  { value: "discount-desc", label: "Biggest Discount" },
  { value: "price-asc", label: "Low – High Price" },
  { value: "price-desc", label: "High – Low Price" },
  { value: "newest", label: "Newest" },
];

interface FilterGroup {
  label: string;
  key: string;
  items: [string, number][];
  /** What to print for a value, where it differs from the value filtered on. */
  labels?: Record<string, string>;
}

interface FilterSidebarProps {
  totalCount: number;
  sort: SortOption;
  onSortChange: (sort: SortOption) => void;
  priceRange: { min: number; max: number };
  priceFilter: [number, number] | null;
  onPriceFilterChange: (range: [number, number] | null) => void;
  filterGroups: FilterGroup[];
  selectedFilters: Record<string, string[]>;
  onToggleFilter: (groupKey: string, value: string) => void;
  activeFilterCount: number;
  onClearAll: () => void;
  expandedFilters: Record<string, boolean>;
  onToggleSection: (section: string) => void;
  onSaleOnly?: boolean;
  onToggleSaleOnly?: () => void;
  isSalePage?: boolean;
}

export const CategoryFilterSidebar = ({
  totalCount,
  sort,
  onSortChange,
  priceRange,
  priceFilter,
  onPriceFilterChange,
  filterGroups,
  selectedFilters,
  onToggleFilter,
  activeFilterCount,
  onClearAll,
  expandedFilters,
  onToggleSection,
  onSaleOnly,
  onToggleSaleOnly,
  isSalePage,
}: FilterSidebarProps) => {
  const sortOptions = isSalePage ? saleSortOptions : defaultSortOptions;
  // The thumbs move on their own; the listing is asked for once, when the drag
  // is released. Reported on every step, a one-second drag on a phone sent 25
  // Storefront requests. The short wait also covers a held arrow key, which
  // commits at every step.
  const [draft, setDraft] = useState<[number, number] | null>(null);
  const commitTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(commitTimer.current), []);
  const shownPrice = draft ?? priceFilter ?? [priceRange.min, priceRange.max];
  const commitPrice = (range: [number, number]) => {
    window.clearTimeout(commitTimer.current);
    commitTimer.current = window.setTimeout(() => {
      onPriceFilterChange(range);
      setDraft(null);
    }, 300);
  };
  return (
    <div className="space-y-6 md:sticky md:top-24 md:self-start md:max-h-[calc(100vh-6rem)] md:overflow-y-auto md:pr-2">

      {/* Header */}
      <div>
        <h2 className="text-sm font-bold text-foreground">Sort & Filter</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{totalCount.toLocaleString()} {totalCount === 1 ? "item" : "items"}</p>
        <div className="border-b border-border mt-3" />
      </div>

      {/* Sort by */}
      <div>
        <button
          onClick={() => onToggleSection("sort")}
          className="flex items-center justify-between w-full text-[13px] font-semibold text-foreground mb-3"
        >
          Sort by
          <ChevronDown className={`h-4 w-4 transition-transform ${expandedFilters.sort !== false ? "rotate-180" : ""}`} />
        </button>
        {expandedFilters.sort !== false && (
          <div className="space-y-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onSortChange(option.value)}
                className="flex w-full items-center gap-2.5 cursor-pointer group text-left"
              >
                <span
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    sort === option.value
                      ? "border-primary"
                      : "border-muted-foreground/40 group-hover:border-foreground"
                  }`}
                >
                  {sort === option.value && (
                    <span className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </span>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* On Sale toggle */}
      {onToggleSaleOnly && !isSalePage && (
        <div>
          <label className="flex items-center gap-2.5 cursor-pointer group">
            <Checkbox
              checked={onSaleOnly}
              onCheckedChange={onToggleSaleOnly}
              aria-label="Show sale items only"
              className="rounded-none"
            />
            <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors font-medium">
              On Sale Only
            </span>
          </label>
        </div>
      )}

      {/* Dynamic filter groups */}
      {filterGroups.map((group) => (
        group.items.length > 0 && (
          <div key={group.key}>
            <button
              onClick={() => onToggleSection(group.key)}
              className="flex items-center justify-between w-full text-[13px] font-semibold text-foreground mb-3"
            >
              {group.label}
              <ChevronDown className={`h-4 w-4 transition-transform ${expandedFilters[group.key] ? "rotate-180" : ""}`} />
            </button>
            {expandedFilters[group.key] && (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {group.items.map(([value, count]) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer group">
                    <Checkbox
                      checked={selectedFilters[group.key]?.includes(value) || false}
                      onCheckedChange={() => onToggleFilter(group.key, value)}
                      aria-label={`Filter by ${group.label}: ${group.labels?.[value] ?? value}`}
                      className="rounded-none"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors flex-1 truncate">
                      {group.labels?.[value] ?? value}
                    </span>
                    {count > 0 && <span className="text-[11px] text-muted-foreground">({count})</span>}
                  </label>
                ))}
              </div>
            )}
          </div>
        )
      ))}

      {/* Price Range */}
      <div>
        <button
          onClick={() => onToggleSection("price")}
          className="flex items-center justify-between w-full text-[13px] font-semibold text-foreground mb-3"
        >
          Price Range
          <ChevronDown className={`h-4 w-4 transition-transform ${expandedFilters.price ? "rotate-180" : ""}`} />
        </button>
        {expandedFilters.price && (
          <div className="space-y-3">
            <Slider
              min={priceRange.min}
              max={priceRange.max}
              step={10}
              value={shownPrice}
              onValueChange={(v) => setDraft(v as [number, number])}
              onValueCommit={(v) => commitPrice(v as [number, number])}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>${shownPrice[0]}</span>
              <span>${shownPrice[1]}</span>
            </div>
          </div>
        )}
      </div>

      {activeFilterCount > 0 && (
        <button onClick={onClearAll} className="text-xs text-primary hover:underline">
          Clear all filters
        </button>
      )}
    </div>
  );
};

export type { SortOption, FilterGroup };
